import { i18n } from '@/i18n';
import {
  localizedPath,
  normalizeSiteLocale,
  seoPageFamilyFromPath,
  siteLocaleFromPath,
  stripLocalePrefix,
} from '@/lib/site-locale';

const NON_LOCALIZED_PATHS = new Set(['/auth/callback', '/index2', '/index3', '/pricing_dev']);
const LOCALIZED_EXACT_PATHS = new Set([
  '/',
  '/about',
  '/contact',
  '/cookie-policy',
  '/forgot',
  '/login',
  '/payment/success',
  '/pricing',
  '/privacy',
  '/signup',
  '/terms',
  '/try',
]);
const LOCALIZED_PREFIXES = [
  '/ai-design-tools',
  '/blog',
  '/help',
  '/learn',
  '/solutions',
  '/spotlight',
];

function isLocalizedPublicPath(path: string): boolean {
  return (
    LOCALIZED_EXACT_PATHS.has(path) ||
    LOCALIZED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
  );
}

export default defineNuxtRouteMiddleware((to) => {
  const unprefixedPath = stripLocalePrefix(to.path);
  if (NON_LOCALIZED_PATHS.has(unprefixedPath)) return;

  const pathLocale = siteLocaleFromPath(to.path);
  if (pathLocale) {
    if (!isLocalizedPublicPath(unprefixedPath)) {
      return navigateTo(
        { path: unprefixedPath, query: to.query, hash: to.hash },
        { redirectCode: 302 }
      );
    }
    i18n.options.locale = pathLocale;
    return;
  }

  if (!isLocalizedPublicPath(unprefixedPath)) return;

  const isSpotlight = unprefixedPath === '/spotlight' || unprefixedPath.startsWith('/spotlight/');
  const isBlog = unprefixedPath === '/blog' || unprefixedPath.startsWith('/blog/');
  const isTools = Boolean(seoPageFamilyFromPath(to.path));
  const segments = unprefixedPath.split('/').filter(Boolean);
  const isCategory = segments.length === 3 && segments[1] === 'categories';
  const isPagination =
    segments.length === 3 && segments[1] === 'page' && /^\d+$/.test(segments[2] ?? '');
  const isLocalizedContentDetail =
    (isSpotlight || isBlog || isTools) && segments.length > 1 && !isCategory && !isPagination;

  if (isLocalizedContentDetail) {
    // Unprefixed Sanity detail URLs are the English canonical. Translated
    // sibling slugs may differ and are selected through alternate metadata.
    i18n.options.locale = 'en';
    return;
  }

  const preferredLocale = normalizeSiteLocale(i18n.locale) ?? 'en';
  if (preferredLocale === 'en') {
    i18n.options.locale = 'en';
    return;
  }

  return navigateTo(
    {
      path: localizedPath(preferredLocale, unprefixedPath),
      query: to.query,
      hash: to.hash,
    },
    { redirectCode: 302 }
  );
});
